import React, {
  PropsWithChildren,
  useCallback,
  useMemo,
  useState,
} from 'react';
import { Campaign } from 'types';
import { getPhaseByType } from 'utils/models/Campaign';
import {
  getPhaseDateHasPassed,
  PHASE_TYPE_OTHERS,
} from 'utils/models/PerformanceUtils';
import { v4 as uuidv4 } from 'uuid';

export interface CampaignReportsContextType {
  campaignId: number;
  hasSurvey: boolean;
  canPublish: boolean;
  engagementSurveyListVersion: string;
  refreshEngagementSurveyListVersion: () => void;
}
const CampaignReportsContext =
  React.createContext<CampaignReportsContextType | null>(null);

type CampaignReportsProviderProps = PropsWithChildren<{
  campaignId: number;
  campaign?: Campaign;
}>;

const CampaignReportsProvider = ({
  campaignId,
  campaign,
  children,
}: CampaignReportsProviderProps) => {
  const [engagementSurveyListVersion, setEngagementSurveyListVersion] =
    useState(uuidv4());

  const hasSurvey = campaign?.properties?.has_survey ?? false;

  const canPublish = useMemo(() => {
    if (!campaign) return false;

    const othersEndDate = getPhaseByType(campaign, PHASE_TYPE_OTHERS)?.end_date;
    if (!othersEndDate) {
      return false;
    }

    return getPhaseDateHasPassed(othersEndDate);
  }, [campaign]);

  const refreshEngagementSurveyListVersion = useCallback(
    () => setEngagementSurveyListVersion(uuidv4()),
    []
  );

  const value: CampaignReportsContextType = React.useMemo(
    () => ({
      campaignId,
      engagementSurveyListVersion,
      hasSurvey,
      canPublish,
      refreshEngagementSurveyListVersion,
    }),
    [
      campaignId,
      hasSurvey,
      engagementSurveyListVersion,
      canPublish,
      refreshEngagementSurveyListVersion,
    ]
  );

  return (
    <CampaignReportsContext.Provider value={value}>
      {children}
    </CampaignReportsContext.Provider>
  );
};

const useCampaignReports = (): CampaignReportsContextType => {
  const context = React.useContext(CampaignReportsContext);
  if (!context) {
    throw new Error(
      'useCampaignReports must be used within a CampaignReportsProvider'
    );
  }
  return context;
};

export { CampaignReportsProvider, useCampaignReports };
